import { useCallback, useEffect, useMemo, useState } from 'react';
import { V2 } from './ctx';
import { buildModel } from './model';
import { injectTheme } from './theme';
import { Toast, ICONS } from './ui';
import Account from './Account';
import { CatatBayarSheet, UbahSheet, TambahSheet, GantiTrukSheet, KoreksiSheet } from './sheets';
import { FixPrevSheet } from './Fixes';
import { BankEntrySheet } from './BankEntry';
import { PiutangSheet } from './PiutangSheet';
import { UbahBarisSheet } from './UbahBaris';
import Ringkasan from './Ringkasan';
import BukuKas from './BukuKas';
import Perubahan from './Perubahan';
import Lainnya from './Lainnya';
import Analisis from './Analisis';

const TABS = ['Ringkasan', 'Buku Kas', 'Perubahan', 'Analisis', 'Lainnya'];
const slug = t => t.toLowerCase().replace(' ', '-');
const tabFromHash = () => TABS.find(t => `#${slug(t)}` === window.location.hash) || 'Ringkasan';
const SHEETS = { catat: CatatBayarSheet, ubah: UbahSheet, tambah: TambahSheet, truk: GantiTrukSheet, koreksi: KoreksiSheet, fixprev: FixPrevSheet, bankentry: BankEntrySheet, piutang: PiutangSheet, baris: UbahBarisSheet };

// Shell for the four-tab redesign (2026-10-01): sidebar on wide screens,
// bottom tab bar on phones, one month model shared by every screen, and a
// single place that opens sheets and shows the toast after an action.
export default function AppV2({ api, user, monthKey, setMonthKey, data, loading, refresh, logout, appVersion, setUi, legacy }) {
  useEffect(() => { injectTheme(); }, []);
  const [tab, setTab] = useState(tabFromHash);
  const [sheet, setSheet] = useState(null);
  const [toast, setToast] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [isPhone, setIsPhone] = useState(() => window.matchMedia('(max-width:900px)').matches);
  useEffect(() => { const m = window.matchMedia('(max-width:900px)'); const f = () => setIsPhone(m.matches); m.addEventListener('change', f); return () => m.removeEventListener('change', f); }, []);
  useEffect(() => { if (window.location.hash !== `#${slug(tab)}`) window.history.replaceState(null, '', `#${slug(tab)}`); }, [tab]);
  useEffect(() => { const f = () => setTab(tabFromHash()); window.addEventListener('hashchange', f); return () => window.removeEventListener('hashchange', f); }, []);

  const model = useMemo(() => buildModel(data), [data]);
  // the whole business, BCA + OCBC (Ringkasan's month-end cards, Buku Kas › Semua)
  const tot = useMemo(() => buildModel(data, 'Semua'), [data]);
  // Accurate Online balances (read-only, optional): loads after the page,
  // never blocks it. null = loading, {available:false} = not available.
  const [acc, setAcc] = useState(null);
  useEffect(() => { let live = true; const c = new AbortController(); setAcc(null); api.accurate(monthKey, c.signal).then(r => live && setAcc(r)).catch(() => live && setAcc({ available: false })); return () => { live = false; c.abort(); }; }, [api, monthKey]);
  // Bank match (Accurate entries vs paid lines); refetched after any change.
  const [match, setMatch] = useState(null);
  useEffect(() => { let live = true; const c = new AbortController(); api.bankMatch(monthKey, c.signal).then(r => live && setMatch(r)).catch(() => live && setMatch({ available: false })); return () => { live = false; c.abort(); }; }, [api, monthKey, refreshKey]);
  // OCBC's own match (its entries vs its lines), for Buku Kas › OCBC / Semua
  const [matchOcbc, setMatchOcbc] = useState(null);
  useEffect(() => { let live = true; const c = new AbortController(); api.bankMatch(monthKey, c.signal, 'ocbc').then(r => live && setMatchOcbc(r)).catch(() => live && setMatchOcbc({ available: false })); return () => { live = false; c.abort(); }; }, [api, monthKey, refreshKey]);
  // Plan receipts ↔ Accurate invoices (server/services/piutang.js).
  const [piut, setPiut] = useState(null);
  useEffect(() => { let live = true; const c = new AbortController(); api.piutang(monthKey, c.signal).then(r => live && setPiut(r)).catch(() => live && setPiut({ available: false })); return () => { live = false; c.abort(); }; }, [api, monthKey, refreshKey]);
  // Fleet forecast (server/services/forecast.js): the running month only.
  // Fresh after a change to the plan, since it fills only what the plan lacks.
  const [fc, setFc] = useState(null);
  useEffect(() => {
    let live = true; const c = new AbortController();
    if (monthKey !== new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 7)) { setFc({ available: false }); return () => {}; }
    api.forecast(monthKey, c.signal, refreshKey > 0).then(r => live && setFc(r)).catch(() => live && setFc({ available: false }));
    return () => { live = false; c.abort(); };
  }, [api, monthKey, refreshKey]);
  // Trucks and their downtime (services/trucks.js): GPS state, open workshop ticket, planned stops.
  const [trucks, setTrucks] = useState(null);
  useEffect(() => { let live = true; const c = new AbortController(); api.trucks(c.signal).then(r => live && setTrucks(r.trucks)).catch(() => live && setTrucks([])); return () => { live = false; c.abort(); }; }, [api, refreshKey]);
  const role = user.role;
  const reload = useCallback(async () => { await refresh(); setRefreshKey(k => k + 1); }, [refresh]);
  const open = useCallback((kind, props = {}) => setSheet({ kind, props }), []);
  // Resolves once the refreshed month is on screen; a sheet stays open on
  // "Menyimpan…" until then so the same row can't be submitted twice.
  const done = useCallback(async msg => { await reload(); setSheet(null); setToast(msg || 'Tersimpan'); }, [reload]);
  const clearToast = useCallback(() => setToast(''), []);
  // intent: where to land inside the tab, e.g. Ringkasan's GPS warning → Buku Kas › Daftar filtered to Cek GPS.
  const [intent, setIntent] = useState(null);
  const go = useCallback((t, i = null) => { setIntent(i); setTab(t); window.scrollTo(0, 0); }, []);
  const shift = n => { const [y, m] = monthKey.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); setMonthKey(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };
  const badge = (model?.items.length || 0) + (role === 'director' ? (data?.reopen?.pending || 0) : 0);
  const monthLabel = legacy.toMonthLabel(monthKey);

  const ctx = { api, user, role, model, tot, data, monthKey, planId: data?.plan?.id, legacy, refresh: reload, refreshKey, open, done, go, intent, acc, match, matchOcbc, piut, fc, trucks, isPhone, logout, setUi, appVersion };
  const Active = { Ringkasan, 'Buku Kas': BukuKas, Perubahan, Analisis, Lainnya }[tab];
  const S = sheet && SHEETS[sheet.kind];
  const month = <div className="v2-month"><button type="button" aria-label="Bulan sebelumnya" onClick={() => shift(-1)}>‹</button><span>{monthLabel}</span><button type="button" aria-label="Bulan berikutnya" onClick={() => shift(1)}>›</button></div>;

  return (
    <V2.Provider value={ctx}>
      <div className="v2 v2-shell">
        <aside className="v2-side">
          {/* Same mark + two-line lockup as the ops app's sidebar (logo-mark.png = the
              ops favicon PNG, not redrawn). Branding is "Sumber Kasih Lestari",
              not "SKL Logistics" (Director, 3 Oct). */}
          <div className="v2-brand"><img src="/logo-mark.png" alt="" /><div><b>Arus Kas</b><small>PT Sumber Kasih Lestari</small></div></div>
          {/* Analisis has its own month picker (it opens on the last closed month): one picker per screen. */}
          {tab !== 'Analisis' && month}
          <nav className="v2-navg">
            {TABS.map(t => <button key={t} type="button" className={`v2-nav ${tab === t ? 'on' : ''}`} onClick={() => go(t)}><span className="v2-nav-l">{ICONS[t]}{t}</span>{t === 'Perubahan' && badge > 0 && <span className="v2-badge">{badge}</span>}</button>)}
          </nav>
          {/* Cross-links to the rest of the SKL suite, same row as the ops app's
              sidebar footer ("Hub ↗ · Cashflow ↗ · Keluar"). Same Azure AD
              tenant, so clicking through is normally a silent re-auth. */}
          {/* Account corner, same in Arus Kas and Ops; the Hub is the account home (15 Oct). */}
          <Account user={user} role={role} logout={logout} lastSync={match?.lastSync} />
        </aside>
        <header className="v2-topbar"><b>{tab}</b>{tab !== 'Analisis' && month}</header>
        <main className="v2-main">
          {/* data still belongs to the previous month right after ‹ › : don't render (or let a sheet submit) against the wrong plan */}
          {/* no data yet = still loading, never "Belum ada plan" (that's only for a month whose loaded data has no plan) */}
          {!data || loading && !model || (data.monthKey && data.monthKey !== monthKey) ? <div className="v2-page"><p className="hint">Memuat…</p></div> : <Active key={monthKey} />}
        </main>
        <nav className="v2-tabbar">
          {TABS.map(t => <button key={t} type="button" className={tab === t ? 'on' : ''} onClick={() => go(t)}>{ICONS[t]}<span>{t}</span>{t === 'Perubahan' && badge > 0 && <span className="v2-badge">{badge}</span>}</button>)}
        </nav>
        {S && model && data?.monthKey === monthKey && <S {...sheet.props} onClose={() => setSheet(null)} />}
        <Toast msg={toast} onDone={clearToast} />
      </div>
    </V2.Provider>
  );
}
